import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Img } from "@/components/ui/img";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/dialog";
import { AdminDialogHeading } from "@/components/admin/AdminDialogHeading";
import { throwIfNotOk } from "@/lib/api-error";
import { toast } from "@/lib/toast";
import { cn } from "@/lib/utils";
import {
  ArrowLeft,
  CheckCircle,
  Power,
  User,
  XCircle,
} from "@phosphor-icons/react";
import { THERAPIST_STATUS_CONFIG_SINGULAR } from "@/lib/status-configs";
import type { Therapist } from "@/types";

/**
 * Sticky identity and decision bar.
 *
 * Both jobs this page serves end in an action, so the actions stay pinned to
 * the top instead of sitting in a card the admin has to scroll back up to
 * reach. Everything descriptive lives in the dossier below.
 */
export function TherapistCommandBar({ therapist }: { therapist: Therapist }) {
  const queryClient = useQueryClient();
  const status = THERAPIST_STATUS_CONFIG_SINGULAR[therapist.status];
  const isDeactivated = Boolean(therapist.deactivatedAt);

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [deactivateReason, setDeactivateReason] = useState("");
  const [cancelSessions, setCancelSessions] = useState(false);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["admin", "therapist", therapist.id] });
    queryClient.invalidateQueries({ queryKey: ["admin", "therapists"] });
  };

  const statusMutation = useMutation({
    mutationFn: async (vars: { status: "approved" | "rejected"; reason?: string }) => {
      const res = await fetch(`/api/therapists/${therapist.id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: vars.status, rejectionReason: vars.reason }),
      });
      await throwIfNotOk(res, "Greška pri ažuriranju statusa");
      return res.json();
    },
    onSuccess: (_data, vars) => {
      toast.success(vars.status === "approved" ? "Terapeut odobren" : "Terapeut odbijen");
      invalidate();
      setRejectOpen(false);
      setRejectReason("");
    },
  });

  const activationMutation = useMutation({
    mutationFn: async (vars: {
      active: boolean;
      reason?: string;
      cancelFutureSessions?: boolean;
    }) => {
      const res = await fetch(`/api/therapists/${therapist.id}/activation`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(vars),
      });
      await throwIfNotOk(res, "Greška pri promjeni aktivacije");
      return res.json();
    },
    onSuccess: (_data, vars) => {
      toast.success(vars.active ? "Terapeut reaktiviran" : "Terapeut deaktiviran");
      invalidate();
      setDeactivateOpen(false);
      setDeactivateReason("");
      setCancelSessions(false);
    },
  });

  const futureCount = therapist.futureSessionCount ?? 0;
  const busy = statusMutation.isPending || activationMutation.isPending;

  return (
    <div className="sticky -top-4 z-20 -mx-4 -mt-4 border-b border-border/70 bg-background/85 px-4 pb-3 pt-7 backdrop-blur sm:-top-6 sm:-mx-6 sm:-mt-6 sm:pt-9">
      <Link
        to="/dashboard/admin/therapists"
        className="mb-2.5 inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="size-3.5" />
        Svi terapeuti
      </Link>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <div
            className={cn(
              "flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-lg",
              status.bg
            )}
          >
            {therapist.profileImageUrl ? (
              <Img
                src={therapist.profileImageUrl}
                alt={therapist.userName}
                className="size-full object-cover"
              />
            ) : (
              <User className={cn("size-5", status.color)} />
            )}
          </div>

          <div className="min-w-0">
            <h1
              className="truncate text-lg font-semibold leading-tight tracking-tight text-foreground"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              {therapist.userName}
            </h1>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <span
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1",
                  status.bg,
                  status.color,
                  status.ring
                )}
              >
                <span className={cn("size-1.5 rounded-full", status.dot)} />
                {status.label}
              </span>
              {isDeactivated && (
                <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700 ring-1 ring-amber-500/20">
                  Deaktiviran
                </span>
              )}
              {therapist.isVolunteer && (
                <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                  Volonter
                </span>
              )}
              {therapist.status === "approved" &&
                !isDeactivated &&
                therapist.acceptingNewClients === false && (
                  <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                    Ne prima nove klijente
                  </span>
                )}
            </div>
          </div>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {therapist.status === "pending" && (
            <>
              <Button
                size="sm"
                onClick={() => statusMutation.mutate({ status: "approved" })}
                disabled={busy}
              >
                <CheckCircle className="size-4" />
                Odobri
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setRejectOpen(true)}
                disabled={busy}
                className="border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
              >
                <XCircle className="size-4" />
                Odbij
              </Button>
            </>
          )}

          {therapist.status === "approved" &&
            (isDeactivated ? (
              <Button
                size="sm"
                onClick={() => activationMutation.mutate({ active: true })}
                disabled={busy}
              >
                <Power className="size-4" />
                Reaktiviraj
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setDeactivateOpen(true)}
                disabled={busy}
                className="border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
              >
                <Power className="size-4" />
                Deaktiviraj
              </Button>
            ))}

          {therapist.status === "rejected" && (
            <Button
              size="sm"
              onClick={() => statusMutation.mutate({ status: "approved" })}
              disabled={busy}
            >
              <CheckCircle className="size-4" />
              Ipak odobri
            </Button>
          )}
        </div>
      </div>

      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)}>
        <DialogHeader onClose={() => setRejectOpen(false)}>
          <AdminDialogHeading
            icon={XCircle}
            title="Odbij terapeuta"
            description="Terapeut dobija obavijest da prijava nije prihvaćena. Razlog mu pomaže da zna šta da ispravi."
            tone="destructive"
          />
        </DialogHeader>
        <DialogBody>
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Razlog odbijanja (opcionalno)..."
            rows={3}
          />
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => setRejectOpen(false)}>
            Odustani
          </Button>
          <Button
            variant="destructive"
            onClick={() =>
              statusMutation.mutate({
                status: "rejected",
                reason: rejectReason || undefined,
              })
            }
            disabled={statusMutation.isPending}
          >
            Odbij terapeuta
          </Button>
        </DialogFooter>
      </Dialog>

      <Dialog open={deactivateOpen} onClose={() => setDeactivateOpen(false)}>
        <DialogHeader onClose={() => setDeactivateOpen(false)}>
          <AdminDialogHeading
            icon={Power}
            title="Deaktiviraj terapeuta"
            description={`Terapeut se uklanja iz pretrage i ne može primati nove rezervacije.${
              futureCount > 0
                ? ` Ima ${futureCount} zakazanih sesija u budućnosti.`
                : " Nema zakazanih sesija u budućnosti."
            }`}
            tone="destructive"
          />
        </DialogHeader>
        <DialogBody>
          <div className="flex flex-col gap-4">
            {futureCount > 0 && (
              <label className="flex items-start gap-2 text-sm">
                <Checkbox
                  checked={cancelSessions}
                  onCheckedChange={(v) => setCancelSessions(v === true)}
                />
                <span>Otkaži sve buduće sesije, klijenti dobijaju obavijest</span>
              </label>
            )}
            <Textarea
              value={deactivateReason}
              onChange={(e) => setDeactivateReason(e.target.value)}
              placeholder="Razlog deaktivacije (opcionalno)..."
              rows={3}
            />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => setDeactivateOpen(false)}>
            Odustani
          </Button>
          <Button
            variant="destructive"
            onClick={() =>
              activationMutation.mutate({
                active: false,
                reason: deactivateReason || undefined,
                cancelFutureSessions: cancelSessions,
              })
            }
            disabled={activationMutation.isPending}
          >
            Deaktiviraj
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}
